import { dateFormatter } from './../../../../../utils/helpers/index';
import React from "react";
import TicketsIcon from "app/components/Icons/TicketsIcon";
import StatusIcon from "app/components/Icons/StatusIcon";
import CalendarIcon from "app/components/Icons/CalendarIcon";
import FlagIcon from "app/components/Icons/FlagIcon";
import ReleaseIcon from "app/components/Icons/ReleaseIcon";
import TargetReleaseIcon from "app/components/Icons/TargetReleaseIcon";
import SprintIcon from "app/components/Icons/SprintIcon";
import EnvironmentIcon from "app/components/Icons/EnvironmentIcon";
import SeverityIcon from "app/components/Icons/SeverityIcon";
import ComponentIcon from "app/components/Icons/ComponentIcon";
import UserIcon from "app/components/Icons/UserIcon";
import { SprintStatus, Filter, BoardType, GroupByTypeEnum, TicketGroup } from "./types"
import toastService from "utils/toast";
import { BacklogIcon, BarChartIcon, CreatorIcon, EpicIcon, TagIcon } from "app/components/Icons";
import TimeIcon from "app/components/Icons/TimeIcon";
import { format, isToday, isTomorrow, parseISO, differenceInCalendarDays, isPast, isThisWeek } from "date-fns";
import { PlanningTypes } from "./containers/Backlog/types";
import StoryIcon from "app/components/Icons/StoryIcon";
import { IMember } from '../ProjectSettings/types';

export const getSprintStatusClasses = (status: SprintStatus) => {
    switch (status) {
        case SprintStatus.CURRENT:
            return 'bg-success100 text-success'
        case SprintStatus.PREVIOUS:
            return 'bg-danger100 text-danger'
        case SprintStatus.NEXT:
        return 'bg-purple100 text-purple'
        default:
        return ''
    }
}

export const Filters: Filter[] = [
    {
      id: "ticket_type",
      name: "Ticket Type",
      icon: React.createElement(TicketsIcon, { color: '#79808A', size: 20 }),
      options: ["Tasks", "Bugs", "Stories", "Epics"],
      isSearchable: false
    },
    {
      id: "status",
      name: "Status",
      icon: React.createElement(StatusIcon, { color: '#79808A', size: 20 }),
      options: ["Open", "In Progress", "In Review", "Close", "Ready for develpement", "Ready for QA", "Ready for release"],
      isSearchable: false
    },
    {
      id: "due_date",
      name: "Due Date",
      icon: React.createElement(CalendarIcon, { color: '#79808A', size: 20 }),
      options: [],
      isSearchable: false
    },
    {
      id: "priority",
      name: "Priority",
      icon: React.createElement(FlagIcon, { color: '#79808A', size: 20 }),
      options: ["Low", "Normal", "High", "Urgent"],
      isSearchable: false
    },
    {
      id: "release",
      name: "Release",
      icon: React.createElement(ReleaseIcon, { color: '#79808A', size: 20 }),
      options: ["1.0", "1.2", "1.3"],
      isSearchable: false
    },
    {
      id: "target_release",
      name: "Target release",
      icon: React.createElement(TargetReleaseIcon, { color: '#79808A', size: 20 }),
      options: ["Previous", "Current", "Next", "2.0.0", "2.1.0", "2.2.0"],
      isSearchable: true
    },
    {
      id: "sprint",
      name: "Sprint",
      icon: React.createElement(SprintIcon, { color: '#79808A', size: 20 }),
      options: ["Previous", "Current", "Next", "Bugbusters", "Operation Warp Speed"],
      isSearchable: true
    },
    {
      id: "environment",
      name: "Environment",
      icon: React.createElement(EnvironmentIcon, { color: '#79808A', size: 20 }),
      options: ["Development", "Production", "Staging", "QA"],
      isSearchable: true
    },
    {
      id: "severity",
      name: "Severity",
      icon: React.createElement(SeverityIcon, { color: '#79808A', size: 20 }),
      options: ["Moderate", "Minor", "Major", "Critical"],
      isSearchable: false
    },
    {
      id: "component",
      name: "Component",
      icon: React.createElement(ComponentIcon, { color: '#79808A', size: 20 }),
      options: ["Web app onboarding", "Web app, login process", "Web apps, usermanagement", "Web app, dashboard"],
      isSearchable: true
    },
    {
      id: "assignee",
      name: "Assignee",
      icon: React.createElement(UserIcon, { color: '#79808A', size: 20 }),
      options: [],
      isSearchable: true
    },
    {
      id: "creator",
      name: "Creator",
      icon: React.createElement(UserIcon, { color: '#79808A', size: 20 }),
      options: [],
      isSearchable: true
    },
    {
      id: "creation_date",
      name: "Creation date",
      icon: React.createElement(CalendarIcon, { color: '#79808A', size: 20 }),
      options: [],
      isSearchable: true
    }
  ];

 export const getProjectIdFromCurrentURL=()=> {
    const url = window.location.href;
    const pathSegments = new URL(url).pathname.split("/");
    return pathSegments.find(segment => /^[0-9a-fA-F-]{36}$/.test(segment)) || null;
}

export const getBoardLink = (board, projectId?: string, teamId?: string): string => {
  const baseUrl = window.location.origin;
  let newPath = '';

  if (board.Type === BoardType.Backlog) {
    newPath = `/app/${projectId}/${teamId}/boards/${board._id}/backlog`;
  } else {
    newPath = `/app/${projectId}/${teamId}/boards/${board._id}/${board?.viewType}/board`;
  }

  return `${baseUrl}${newPath}`;
};

export const handleCopyBoardLink = (board, projectId?: string, teamId?: string) => {  
  const boardLink = getBoardLink(board, projectId, teamId);
  navigator.clipboard.writeText(boardLink)
    .then(() => {
      toastService.success(`${board.name} link has been successfully copied.`);
    })
    .catch(() => {
      toastService.error('Failed to copy board link');
    });
}


export const handleRedirectToBoard = (board, projectId?: string, teamId?: string) => {
  const boardLink = getBoardLink(board, projectId, teamId);
  window.location.href = boardLink;
};

export const getTicketGroupKey = (ticket: any, groupBy: string): string => {
  const defaultMap: Record<string, string> = {
    release: "Unassigned",
    sprint: "No Sprint",
    component: "No Component",
    environment: "No Environment",
    severity: "No Severity",
    status: "Unknown",
    priority: "No Priority",
    creator: "No Creator",
    ticketType: "No Ticket Type",
    dueDate: "No Due Date",
    targetRelease: "No Target Release",
    effortsEstimation: "No Estimation",
    effortsActual: "No Actual Effort",
    createdDate: "No Created Date",
    tags: "No Tags",
    none:'Backlog'
  };

  switch (groupBy) {
    case "assignee": {
      const names = ticket.assignedTo?.map((a: any) => a.name).sort();
      return names?.length ? names.join(", ") : "Unassigned";
    }
    case "tags": {
      const names = ticket.tags?.map((l: any) => l.title).sort();
      return names?.length ? names.join(", ") : "UnTagged";
    }
    case "dueDate":
      return getDueDateGroupName(ticket.dueDate);
    case "effortsEstimation":
      return getEffortGroupName(ticket.effortEstimated);
    case "effortsActual":
      return getEffortGroupName(ticket.effortActual);
    case "status":
      return ticket.status?.name || defaultMap.status;
    case "ticketType":
      return ticket.type?.name || defaultMap.ticketType;
    case "creator":
      return ticket.createdBy?.name || defaultMap.creator;
    default:
      return ticket[groupBy] || defaultMap[groupBy] || "Uncategorized";
  }
};




export const getDueDateGroupName = (dueDateStr: string): string => {
  if (!dueDateStr) return "No Due Date";

  const dueDate = parseISO(dueDateStr);
  const today = new Date();

  if (isPast(dueDate) && !isToday(dueDate)) {
    return "Past Due";
  }

  if (isToday(dueDate)) {
    return "Today";
  }

  if (isThisWeek(dueDate, { weekStartsOn: 1 })) {
    return format(dueDate, "EEEE"); // Monday, Tuesday, etc.
  }

  return "Future Date";
};

export const getEffortGroupName = (value: string | number | null | undefined): string => {
  if (!value || value === "0") {
    return "No Efforts";
  }
  let hours = 0;
  if (typeof value === "string") {
    const parts = value.split("/").map(Number).filter((n) => !isNaN(n));
    hours = parts.length === 2 ? (parts[0] / parts[1]) * 8 : parseFloat(value);
  } else if (typeof value === "number") {
    hours = value;
  }

  if (hours <= 5) return "1–5 hours";
  if (hours <= 20) return "6–20 hours";
  return ">20 hours";
};

export const getGroupIcon = (groupName: string) => {
    switch (groupName) {
        case  GroupByTypeEnum.STATUS:
            return React.createElement(StatusIcon, { size: 20 });
        case GroupByTypeEnum.TAGS:
            return React.createElement(TagIcon, { size: 20 });
        case GroupByTypeEnum.ASSIGNEE:
            return React.createElement(UserIcon, { size: 20 });
        case GroupByTypeEnum.TICKET_TYPE:
            return React.createElement(TicketsIcon, { size: 20 });
        case GroupByTypeEnum.DUE_DATE:
            return React.createElement(CalendarIcon, { size: 20 });
        case GroupByTypeEnum.PRIORITY:
            return React.createElement(FlagIcon, { size: 20 });
        case GroupByTypeEnum.SPRINT:
            return React.createElement(SprintIcon, { size: 20 });
        case GroupByTypeEnum.RELEASE:
            return React.createElement(ReleaseIcon, { size: 20 });
        case GroupByTypeEnum.TARGET_RELEASE:
            return React.createElement(TargetReleaseIcon, { size: 20 });
        case GroupByTypeEnum.ENVIRONMENT:
            return React.createElement(EnvironmentIcon, { size: 20 });
        case GroupByTypeEnum.SEVERITY:
            return React.createElement(BarChartIcon, { size: 20 });
        case GroupByTypeEnum.COMPONENT:
            return React.createElement(ComponentIcon, { size: 20 });
        case GroupByTypeEnum.CREATOR:
            return React.createElement(CreatorIcon, { size: 20 });
        case GroupByTypeEnum.CREATED_DATE:
            return React.createElement(CalendarIcon, { size: 20 });
        case GroupByTypeEnum.EFFORTS_ESTIMATION:
            return React.createElement(TimeIcon, { size: 20 });
        case GroupByTypeEnum.EFFORTS_ACTUAL:
            return React.createElement(TimeIcon, { size: 20 });
        case GroupByTypeEnum.NONE:
            return React.createElement(BacklogIcon, { size: 20 });    
        default:
            return null;
    }
}

export const getPlanningTypeIcon = (type: PlanningTypes) => {
    switch (type) {
        case PlanningTypes.COMPONENTS:
            return React.createElement(ComponentIcon, { size: 20 });
        case PlanningTypes.EPICS:
            return React.createElement(EpicIcon, { size: 20 });
        case PlanningTypes.STORIES:
            return React.createElement(StoryIcon, { size: 20 });
        case PlanningTypes.SPRINT:
            return React.createElement(SprintIcon, { size: 20 });
        case PlanningTypes.NEXT_RELEASES:
            return React.createElement(ReleaseIcon, { size: 20 });
        case PlanningTypes.PAST_RELEASES:
            return React.createElement(ReleaseIcon, { size: 20 });
        default:
        return null
    }
}


export function formatLabel(str) {
    return str
      ?.replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters
      ?.replace(/^./, (char) => char.toUpperCase()); // Capitalize first letter
}
  
export const addTicketsLocally = (group: any, allTickets: any[], groupBy: string) => {
  const groupTicketRefs = group?.tickets || [];
  const groupTicketIds = new Set(groupTicketRefs.map((t: any) => t.ticketId));
  const allTicketMap = new Map(allTickets.map((ticket: any) => [ticket._id, ticket]));

  const resolvedTickets: any[] = [];
  let maxOrder = 0;

  // Resolve current group tickets
  for (const item of groupTicketRefs) {
    const ticket = allTicketMap.get(item.ticketId);
    if (ticket) {
      resolvedTickets.push({
        ...ticket,
        order: item.order,
      });
      maxOrder = Math.max(maxOrder, item.order ?? 0);
    }
  }

  // Identify local tickets not in group yet and add it to the group
  const additionalTickets = allTickets.filter(
    (ticket: any) => ticket.groupId === group._id && !groupTicketIds.has(ticket._id)
  ).map((ticket: any, idx: number) => ({
    ...ticket,
    order: maxOrder + idx + 1,
  }));
  return {
    _id: group._id,
    groupName: group.title,
    description: group.description || "",
    color: "#43D98C",
    tickets: [...resolvedTickets, ...additionalTickets],
    isCollapse: false,
    isVisible: group.isVisible,
    order: group.order,
    icon: getGroupIcon(groupBy),
    ticketLimit: 10,
  };
}

export const getDefaultTicketFields = (
  group: TicketGroup,
  groupBy: string,
  members: IMember[] = []
): Record<string, any> => {
  const groupValue = group?.groupName || "";

  const fieldMap: Record<string, any> = {
    status: { status: groupValue },
    priority: { priority: groupValue },
    dueDate: { dueDate: groupValue },
    assignedTo: (() => {
      // find member by name or id matching groupValue
      const match = members.find(
        (m) => m.name.toLowerCase() === groupValue.toLowerCase() || m._id === groupValue
      );
      return match ? { assignedTo: match._id } : {};
    })(),
  };

  return fieldMap[groupBy] || {};
};
